import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Check, AlertTriangle } from 'lucide-react';
import SEO from '../components/seo/SEO';
import Footer from '../components/layout/Footer';
import OpPageHeader from '../components/optimus/OpPageHeader';

interface ContactPageProps {
  darkMode: boolean;
}

const socialLinks = [
  { name: 'GitHub', href: 'https://github.com/tkkalkhaled' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/in/talal-alkhaled-a0779b321/' },
  { name: 'Instagram', href: 'https://www.instagram.com/talalal5aled' },
];

/**
 * Contact page in the Optimus design system. The form mechanics are unchanged:
 * POST JSON {name, email, subject, message} to /api/contact (or /api/contact-dev
 * locally) and expect a {success} response.
 */
const ContactPage: React.FC<ContactPageProps> = ({ darkMode }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    setErrorDetails(null);

    const isLocalDev =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const endpoint = isLocalDev ? '/api/contact-dev' : '/api/contact';

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
      .then(async (response) => {
        let data;
        try {
          data = await response.json();
        } catch {
          throw new Error(`Invalid server response: ${response.status} ${response.statusText}`);
        }
        if (!response.ok || !data.success) {
          throw new Error(data.message || `Error: ${response.status} ${response.statusText}`);
        }
        return data;
      })
      .then(() => {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      })
      .catch((error) => {
        setFormStatus('error');
        setErrorDetails(error.message || 'Unknown error occurred');
      });
  };

  const inputClasses =
    'w-full bg-transparent border border-foreground/15 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-foreground outline-none transition-colors';

  return (
    <main className="op-page noise-overlay relative min-h-screen overflow-x-hidden">
      <SEO
        title={`${t('contact.title')} | Talal Alkhaled`}
        description={t('contact.getInTouch')}
        url="https://talkhaled.com/contact"
      />

      <OpPageHeader
        label={t('op.sub.contactLabel')}
        line1={t('op.sub.contactH1')}
        line2={t('op.sub.contactH2')}
        description={t('op.sub.contactDesc')}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-20 border-t border-foreground/10 pt-12 lg:pt-16">
          {/* Left: contact info */}
          <div className="lg:col-span-2">
            <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase block mb-8">
              {t('contact.contactInformation')}
            </span>

            <div className="space-y-6 mb-12">
              <a
                href="mailto:contact@talkhaled.com"
                className="flex items-start gap-4 group"
              >
                <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-foreground/10 group-hover:bg-foreground group-hover:text-background transition-colors duration-300">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('contact.email')}</div>
                  <div className="font-mono text-sm text-foreground" dir="ltr">contact@talkhaled.com</div>
                </div>
              </a>

              <a href="tel:+96597499111" className="flex items-start gap-4 group">
                <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-foreground/10 group-hover:bg-foreground group-hover:text-background transition-colors duration-300">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('contact.phone')}</div>
                  <div className="font-mono text-sm text-foreground" dir="ltr">+965 97499111</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-foreground/10">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('contact.location')}</div>
                  <div className="text-sm text-foreground">{t('contact.eastLansing')}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-foreground/10">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('contact.timezone')}</div>
                  <div className="text-sm text-foreground">{t('contact.responseTime')}</div>
                </div>
              </div>
            </div>

            <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase block mb-6">
              {t('contact.socialMedia')}
            </span>
            <div className="flex flex-col gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group w-fit"
                >
                  {link.name}
                  <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase block mb-8">
              {t('contact.sendMessage')}
            </span>

            {formStatus === 'success' ? (
              <div className="border border-foreground/10 p-10 text-center">
                <div className="w-12 h-12 mx-auto mb-6 flex items-center justify-center border border-foreground/15 rounded-full">
                  <Check className="w-6 h-6" />
                </div>
                <p className="text-lg text-foreground mb-8 max-w-md mx-auto">
                  {t('contact.form.successMessage')}
                </p>
                <button
                  type="button"
                  onClick={() => setFormStatus('idle')}
                  className="px-6 py-3 border border-foreground/20 text-sm font-medium text-foreground hover:bg-foreground/5 transition-colors"
                >
                  {t('contact.sendMessage')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block font-mono text-xs text-muted-foreground mb-2">
                      {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.form.namePlaceholder')}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block font-mono text-xs text-muted-foreground mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.form.emailPlaceholder')}
                      className={inputClasses}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block font-mono text-xs text-muted-foreground mb-2">
                    {t('contact.form.subject')}
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className={`${inputClasses} appearance-none cursor-pointer`}
                  >
                    <option value="" disabled className="bg-background text-foreground">
                      {t('contact.form.selectSubject')}
                    </option>
                    {['jobOpportunity', 'projectInquiry', 'collaboration', 'other'].map((key) => (
                      <option key={key} value={t(`contact.form.${key}`)} className="bg-background text-foreground">
                        {t(`contact.form.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block font-mono text-xs text-muted-foreground mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    className={`${inputClasses} resize-y min-h-[140px]`}
                  />
                </div>

                {formStatus === 'error' && (
                  <div className="border border-foreground/20 bg-foreground/[0.03] p-4 flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="text-foreground">
                        {t('contact.form.errorMessage')} {errorDetails}
                      </p>
                      <a
                        href="mailto:contact@talkhaled.com"
                        className="underline underline-offset-4 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t('contact.form.sendDirectly')}
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-6 pt-2">
                  <p className="font-mono text-xs text-muted-foreground hidden sm:block">
                    {t('op.cta.note')}
                  </p>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-8 h-12 bg-foreground text-background text-sm font-medium rounded-full hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? t('contact.form.sending') : t('contact.form.sendButton')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer darkMode={darkMode} />
    </main>
  );
};

export default ContactPage;
